import CircleCheck from '@avanta/shared/assets/circle-check.svg';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useLocation, useNavigate } from 'react-router';
import Logo from '@/assets/itae-agendar.svg';
import { SupportButton } from 'components/SupportButton';
import { ROUTES } from 'constants/routes';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { AppointmentSummary } from './AppointmentSummary';

export const AppointmentsSuccess = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const location = useLocation();
  const patientId = location.state?.patientId ?? '';
  const hasExpedient = location.state?.hasExpedient ?? false;
  const { data: patientRecord } = useFetchActivePatientRecord(patientId, { polling: true });
  const navigate = useNavigate();
  const patientRecordCode = patientRecord?.code;

  const handleOnNavigate = useCallback((): void => {
    navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patientId }));
  }, [navigate, patientId]);

  useEffect(() => {
    if (!patientRecordCode) {
      return;
    }
    const timeoutId = setTimeout(() => {
      handleOnNavigate();
    }, 5000);

    return (): void => clearTimeout(timeoutId);
  }, [handleOnNavigate, patientRecordCode, patientRecord]);

  const body = !hasExpedient
    ? t('AppointmentsSuccess.notExpedient.body')
    : t('AppointmentsSuccess.hasExpedient.body');

  return (
    <>
      <header className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 am-xl:px-18">
        <Link to={ROUTES.HOME} className="flex items-center">
          <img src={Logo} alt="itae" className="h-12 w-auto object-contain" />
        </Link>
        <SupportButton />
      </header>
      <main className="flex flex-col items-center px-4 pt-8 pb-12 am-xl:pt-12">
        <img src={CircleCheck} alt="" className="mb-4 size-28" />
        <div className="mb-4 flex flex-col items-center gap-2 text-center">
          <Text tag="h1" variant="heading-sm-medium" className="text-denim-500">
            {t('AppointmentsSuccess.title')}
          </Text>
          <Text variant="text-md-regular" className="text-gray-500">
            {body}
          </Text>
        </div>
        <AppointmentSummary />
        <Button variant="link" className="mt-6 h-auto gap-1 p-0" onClick={handleOnNavigate}>
          {t('AppointmentsSuccess.cta')}
          <ChevronRightIcon className="size-4" />
        </Button>
      </main>
    </>
  );
};
